import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, DollarSign, TrendingUp, Users, Download, CreditCard, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

function fmtUSD(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 10_000)    return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtInt(n) {
  return Number(n || 0).toLocaleString();
}

function KpiCard({ icon: Icon, label, value, sub, tone = 'ink', testId }) {
  const toneMap = {
    ink:    { bg: '#0F0F10', fg: '#FFFFFF', accent: '#B38138' },
    green:  { bg: '#F0FDF4', fg: '#0F0F10', accent: '#15803D' },
    red:    { bg: '#FEF2F2', fg: '#0F0F10', accent: '#B91C1C' },
    blue:   { bg: '#EEF4FF', fg: '#0F0F10', accent: '#1E4FD8' },
    gold:   { bg: '#FEF7ED', fg: '#0F0F10', accent: '#B38138' },
    slate:  { bg: '#F4F4F5', fg: '#0F0F10', accent: '#4B4B52' },
  };
  const c = toneMap[tone] || toneMap.ink;
  return (
    <div className="rounded-2xl p-5" style={{ background: c.bg, color: c.fg }} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2 opacity-80">
        <Icon size={14} style={{ color: c.accent }} strokeWidth={2.2} />
        <span className="text-[10px] font-bold uppercase tracking-[1.4px]">{label}</span>
      </div>
      <p className="font-brand text-[28px] font-[500] leading-none tracking-tight">{value}</p>
      {sub && <p className="text-[11.5px] mt-2 opacity-70">{sub}</p>}
    </div>
  );
}

export default function AdminFinancials() {
  const navigate = useNavigate();
  const [rev, setRev] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/revenue');
        setRev(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Admin access required');
        navigate('/admin', { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" />
      </div>
    );
  }
  if (!rev) return null;

  const dl = rev.downloads || {};
  const mom = dl.mom_pct;
  const yoy = dl.yoy_pct;
  const trend = rev.monthly_trend || [];

  // Prefer real Stripe invoice totals when available; otherwise fall back
  // to a derived series (`new_paid × ARPU`) so the chart is still useful
  // in dev / pre-launch environments.
  const stripeTrend = Array.isArray(rev.stripe_trend) ? rev.stripe_trend : null;
  const trendSource = rev.stripe_trend_source || (stripeTrend ? 'stripe' : 'derived');
  let cumMrr;
  if (stripeTrend && stripeTrend.length) {
    let running = 0;
    cumMrr = stripeTrend.map((m) => {
      running += m.gross || 0;
      return { month: m.month, mrr: Math.round(running * 100) / 100 };
    });
  } else {
    let running = 0;
    const avgArpu = rev.arpu || (rev.tier_prices?.premium || 0);
    cumMrr = trend.map((m) => {
      running += (m.new_paid || 0) * avgArpu;
      return { month: m.month, mrr: Math.round(running * 100) / 100 };
    });
  }

  return (
    <div className="min-h-screen bg-white pb-[80px]" data-testid="admin-financials-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/admin')}
          data-testid="fin-back"
          aria-label="Back to admin"
          className="w-9 h-9 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#4B4B52]"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">ADMIN · REVENUE</p>
          <h1 className="font-brand text-[26px] font-[500] tracking-tight text-[#0F0F10] leading-tight">
            Financials
          </h1>
        </div>
      </div>

      <section className="px-5 mb-4">
        <WarmerStatus warmer={rev.stripe_warmer} />
      </section>

      {/* Recurring revenue row */}
      <section className="px-5 mb-6">
        <div className="grid grid-cols-2 gap-3">
          <KpiCard
            icon={DollarSign} tone="ink" testId="kpi-mrr"
            label="MRR" value={fmtUSD(rev.mrr)}
            sub={`${fmtInt(rev.paid_total)} paying members`}
          />
          <KpiCard
            icon={TrendingUp} tone="gold" testId="kpi-arr"
            label="ARR" value={fmtUSD(rev.arr)}
            sub="Annualized run rate"
          />
          <KpiCard
            icon={CreditCard} tone="green" testId="kpi-arpu"
            label="ARPU" value={fmtUSD(rev.arpu)}
            sub="Avg. revenue per paying user"
          />
          <KpiCard
            icon={XCircle} tone="red" testId="kpi-churn"
            label="Churn · 30d" value={fmtInt(rev.churned_30d)}
            sub={`${fmtInt(rev.new_paid_30d)} new paid in 30d`}
          />
        </div>
      </section>

      {/* Downloads / acquisitions */}
      <section className="px-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">ACQUISITION</p>
            <h2 className="font-brand text-[19px] font-[500] tracking-tight text-[#0F0F10]">Downloads · New Registrations</h2>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <KpiCard
            icon={Download} tone="slate" testId="kpi-dl-today"
            label="Today" value={fmtInt(dl.today)}
            sub="New signups today"
          />
          <KpiCard
            icon={Download} tone="blue" testId="kpi-dl-30d"
            label="Monthly" value={fmtInt(dl.monthly)}
            sub={mom == null ? 'No prior data' :
              (mom >= 0 ? `▲ ${mom}% MoM` : `▼ ${Math.abs(mom)}% MoM`)}
          />
          <KpiCard
            icon={Download} tone="ink" testId="kpi-dl-365d"
            label="Annual" value={fmtInt(dl.annual)}
            sub={yoy == null ? 'No prior data' :
              (yoy >= 0 ? `▲ ${yoy}% YoY` : `▼ ${Math.abs(yoy)}% YoY`)}
          />
        </div>
        <p className="text-[10.5px] text-[#8A8A93] mt-2 leading-snug">
          PWA has no native install tracking — new registered users are the acquisition proxy.
        </p>
      </section>

      {/* Tier mix */}
      <section className="px-5 mb-6">
        <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93] mb-3">TIER MIX</p>
        <div className="rounded-2xl border border-black/[0.06] overflow-hidden bg-white" data-testid="tier-mix">
          {[
            { tier: 'free',    label: 'Free',    color: '#4B4B52' },
            { tier: 'premium', label: 'Premium', color: '#15803D' },
            { tier: 'vip',     label: 'VIP',     color: '#B38138' },
          ].map((row) => {
            const count = rev.by_tier?.[row.tier] || 0;
            const total = (rev.by_tier?.free || 0) + (rev.by_tier?.premium || 0) + (rev.by_tier?.vip || 0);
            const pct = total ? (count / total) * 100 : 0;
            const price = rev.tier_prices?.[row.tier] || 0;
            const revBucket = row.tier === 'free' ? 0 : count * price;
            return (
              <div key={row.tier} className="p-4 border-b border-black/[0.06] last:border-0">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: row.color }} />
                    <span className="text-[14px] font-semibold text-[#0F0F10]">{row.label}</span>
                    <span className="text-[11px] text-[#8A8A93]">· {fmtInt(count)} users · {pct.toFixed(1)}%</span>
                  </div>
                  <span className="text-[13px] font-bold tabular-nums text-[#0F0F10]">
                    {row.tier === 'free' ? '—' : fmtUSD(revBucket)}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, background: row.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* MRR trend + Signup trend */}
      <section className="px-5 mb-6">
        <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93] mb-3">12-MONTH TREND</p>
        <div className="rounded-2xl border border-black/[0.06] bg-white p-4" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold text-[#4B4B52]">Cumulative MRR</p>
            <span
              className="text-[9.5px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full"
              style={
                trendSource === 'stripe'
                  ? { background: '#EEF4FF', color: '#1E4FD8' }
                  : { background: '#F4F4F5', color: '#8A8A93' }
              }
              data-testid="mrr-source-pill"
              title={trendSource === 'stripe' && rev.stripe_trend_cached_at
                ? `Cached ${new Date(rev.stripe_trend_cached_at).toLocaleTimeString()}`
                : undefined}
            >
              {trendSource === 'stripe'
                ? (rev.stripe_trend_cached_at ? 'Stripe · cached' : 'Stripe · billed')
                : 'Derived'}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={cumMrr}>
              <defs>
                <linearGradient id="mrrArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#B38138" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#B38138" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#0000000d" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false}
                tickFormatter={(v) => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v}`} />
              <ReTooltip formatter={(v) => fmtUSD(v)} />
              <Area type="monotone" dataKey="mrr" stroke="#B38138" strokeWidth={2} fill="url(#mrrArea)" />
            </AreaChart>
          </ResponsiveContainer>

          <p className="text-[11px] font-semibold text-[#4B4B52] mt-4 mb-2">New signups / month</p>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0000000d" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <ReTooltip formatter={(v) => fmtInt(v)} />
              <Bar dataKey="signups" fill="#1E4FD8" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Fresh paid growth */}
      <section className="px-5 mb-6">
        <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93] mb-3">NEW PAID SUBSCRIBERS</p>
        <div className="grid grid-cols-3 gap-3">
          <KpiCard icon={Users} tone="slate" testId="kpi-paid-today"
            label="Today" value={fmtInt(rev.new_paid_today)} />
          <KpiCard icon={Users} tone="green" testId="kpi-paid-7d"
            label="7 days" value={fmtInt(rev.new_paid_7d)} />
          <KpiCard icon={Users} tone="ink" testId="kpi-paid-30d"
            label="30 days" value={fmtInt(rev.new_paid_30d)} />
        </div>
      </section>

      {/* CAC — Customer Acquisition Cost (Feb 2026) */}
      <CACSection rev={rev} onUpdate={(next) => setRev((r) => ({ ...r, cac: next }))} />

      {/* Store one-time purchases — rich section with period picker,
          trend arrows, per-bundle breakdown, refunds, and best-sellers. */}
      <StoreAddonsSection />
    </div>
  );
}


// ==================== CAC / LTV block ====================
function CACSection({ rev, onUpdate }) {
  const cac = rev?.cac || {};
  const [editing, setEditing] = useState(false);
  const [spend, setSpend] = useState(String(cac.ad_spend_monthly || ''));
  const [ltvM, setLtvM] = useState(String(cac.ltv_months || 12));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/settings/ad-spend', {
        ad_spend_monthly: parseFloat(spend || '0'),
        ltv_months: parseFloat(ltvM || '12'),
      });
      onUpdate({
        ...cac,
        ad_spend_monthly: r.data.ad_spend_monthly,
        ltv_months: r.data.ltv_months,
      });
      // Re-fetch full revenue to refresh CAC/LTV calculations.
      const r2 = await api.get('/admin/revenue');
      onUpdate(r2.data.cac);
      toast.success('Ad spend saved');
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const notConfigured = !cac.ad_spend_monthly || cac.ad_spend_monthly <= 0;
  const ratio = cac.ltv_cac_ratio;
  const ratioTone = ratio == null ? 'slate' : ratio >= 3 ? 'green' : ratio >= 1 ? 'gold' : 'red';

  return (
    <section className="px-5 mb-6" data-testid="cac-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">MARKETING · CAC</p>
          <h2 className="font-brand text-[19px] font-[500] tracking-tight text-[#0F0F10]">Acquisition Cost</h2>
        </div>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            data-testid="cac-edit-btn"
            className="text-[11px] font-semibold text-[#B91C1C] hover:underline"
          >
            {notConfigured ? 'Set ad spend' : 'Edit'}
          </button>
        )}
      </div>

      {editing ? (
        <div className="rounded-2xl border border-black/[0.06] bg-white p-4" data-testid="cac-editor">
          <label className="text-[11px] font-semibold text-[#4B4B52] block mb-1.5">Monthly ad spend (USD)</label>
          <input
            type="number" min="0" step="1"
            value={spend}
            onChange={(e) => setSpend(e.target.value)}
            data-testid="cac-spend-input"
            className="w-full px-3 py-2.5 rounded-lg border border-[var(--hairline-strong)] text-sm focus:outline-none focus:border-[#B91C1C]"
            placeholder="e.g. 5000"
          />
          <label className="text-[11px] font-semibold text-[#4B4B52] block mt-3 mb-1.5">
            Expected paying lifetime (months) <span className="text-[#8A8A93] font-normal">— used for LTV/CAC</span>
          </label>
          <input
            type="number" min="1" step="1"
            value={ltvM}
            onChange={(e) => setLtvM(e.target.value)}
            data-testid="cac-ltv-input"
            className="w-full px-3 py-2.5 rounded-lg border border-[var(--hairline-strong)] text-sm focus:outline-none focus:border-[#B91C1C]"
          />
          <div className="flex gap-2 mt-3">
            <button
              onClick={save}
              disabled={saving}
              data-testid="cac-save-btn"
              className="flex-1 px-4 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => { setEditing(false); setSpend(String(cac.ad_spend_monthly || '')); setLtvM(String(cac.ltv_months || 12)); }}
              className="px-4 py-2.5 rounded-full border border-[var(--hairline-strong)] text-sm font-semibold text-[#4B4B52]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : notConfigured ? (
        <div className="rounded-2xl border border-black/[0.06] bg-[#FAFAFA] p-4 text-center" data-testid="cac-empty">
          <p className="text-[13px] font-semibold text-[#0F0F10]">Set your monthly ad spend to see CAC</p>
          <p className="text-[11.5px] text-[#8A8A93] mt-1">CAC = Ad spend ÷ New paid subscribers. LTV/CAC {'≥'} 3× is healthy.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <KpiCard icon={DollarSign} tone="ink" testId="kpi-cac-30d"
            label="CAC · 30d" value={cac.cac_30d != null ? fmtUSD(cac.cac_30d) : '—'}
            sub={`Ad spend ${fmtUSD(cac.ad_spend_monthly)}/mo`} />
          <KpiCard icon={DollarSign} tone="slate" testId="kpi-cac-7d"
            label="CAC · 7d" value={cac.cac_7d != null ? fmtUSD(cac.cac_7d) : '—'}
            sub="Weekly run-rate estimate" />
          <KpiCard icon={TrendingUp} tone="gold" testId="kpi-ltv-est"
            label="LTV · Est." value={fmtUSD(cac.ltv_est)}
            sub={`ARPU × ${cac.ltv_months || 12} mo`} />
          <KpiCard icon={TrendingUp} tone={ratioTone} testId="kpi-ltv-cac"
            label="LTV / CAC" value={ratio != null ? `${ratio}×` : '—'}
            sub={ratio == null ? 'Needs data' : ratio >= 3 ? 'Healthy' : ratio >= 1 ? 'Watch' : 'Under-priced'} />
        </div>
      )}
    </section>
  );
}


// ==================== Store Add-ons ====================

const PERIODS = [
  { key: '7d',       label: '7 days'   },
  { key: '30d',      label: '30 days'  },
  { key: '90d',      label: '90 days'  },
  { key: 'lifetime', label: 'Lifetime' },
];

const FAMILY_META = {
  intros:      { title: 'Warm Intros',                    emoji: '💬', accent: '#1E4FD8', unitLabel: 'Warm Intro' },
  super_likes: { title: 'Serious Interest Super-Likes',   emoji: '★',  accent: '#B38138', unitLabel: 'Serious Interest' },
  boosts:      { title: 'Meet Priority Boosts',           emoji: '⚡', accent: '#B91C1C', unitLabel: 'Meet Priority' },
};

function bundleQty(key) {
  // product_key convention: prefix_qty → grab the trailing integer.
  const m = String(key || '').match(/(\d+)$/);
  return m ? parseInt(m[1], 10) : 0;
}

function TrendChip({ deltaPct }) {
  if (deltaPct === null || deltaPct === undefined) {
    return <span className="text-[10.5px] text-[#8A8A93] font-[600]">no prior</span>;
  }
  const up = deltaPct > 0;
  const flat = deltaPct === 0;
  const color = flat ? '#8A8A93' : up ? '#15803D' : '#B91C1C';
  const bg    = flat ? '#F4F4F5' : up ? '#F0FDF4' : '#FEF2F2';
  const sign  = up ? '+' : '';
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10.5px] font-[700] px-1.5 py-0.5 rounded-full tabular-nums"
      style={{ color, background: bg }}
    >
      {up ? '↗' : flat ? '→' : '↘'} {sign}{deltaPct}%
    </span>
  );
}

function StoreAddonsSection() {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // { intros: true, ... }

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get('/admin/financials/store-addons', { params: { period } })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) toast.error('Failed to load store add-ons'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [period]);

  const toggle = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));

  return (
    <section className="px-5 mb-10" data-testid="store-revenue-section">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">STORE ADD-ONS</p>
          <h2 className="font-brand text-[19px] font-[500] tracking-tight text-[#0F0F10]">
            One-time purchases
          </h2>
        </div>
        {/* Period toggle */}
        <div
          className="flex items-center gap-0.5 p-0.5 rounded-full bg-black/[0.04]"
          data-testid="store-period-toggle"
        >
          {PERIODS.map((p) => {
            const active = period === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                data-testid={`store-period-${p.key}`}
                className={`px-3 py-1.5 rounded-full text-[11.5px] font-[700] tracking-tight transition-colors ${
                  active ? 'bg-white text-[#0F0F10] shadow-sm' : 'text-[#4B4B52] hover:text-[#0F0F10]'
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#B38138]" /></div>
      ) : data ? (
        <>
          {/* Totals strip */}
          <div
            className="rounded-2xl p-4 mb-4 flex items-center gap-4 bg-[#0F0F10] text-white"
            data-testid="store-totals-strip"
          >
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-[700] uppercase tracking-[1.4px] opacity-70">Gross · {PERIODS.find(p => p.key === period)?.label}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className="font-brand text-[26px] font-[500] leading-none tracking-tight tabular-nums">
                  {fmtUSD(data.totals?.gross)}
                </p>
                <TrendChip deltaPct={data.totals?.delta_pct} />
              </div>
              <p className="text-[11px] opacity-70 mt-1">
                {fmtInt(data.totals?.orders)} orders · {fmtInt(data.totals?.units)} units sold
              </p>
            </div>
            <div className="text-right flex-shrink-0" data-testid="store-refunded">
              <p className="text-[10px] font-[700] uppercase tracking-[1.4px] opacity-70">Refunded</p>
              <p className="font-brand text-[16px] font-[500] leading-none mt-1 tabular-nums" style={{ color: (data.totals?.refunded_gross || 0) > 0 ? '#FCA5A5' : '#FFFFFF' }}>
                {fmtUSD(data.totals?.refunded_gross)}
              </p>
            </div>
          </div>

          {/* Per-family pills with expandable bundle breakdown */}
          <div className="grid grid-cols-1 gap-3" data-testid="store-family-list">
            {Object.entries(FAMILY_META).map(([key, meta]) => {
              const p = data.products?.[key] || {};
              const isOpen = !!expanded[key];
              return (
                <div
                  key={key}
                  className="rounded-2xl bg-white overflow-hidden"
                  style={{ border: '1px solid rgba(15,15,16,0.06)' }}
                  data-testid={`store-family-${key}`}
                >
                  <button
                    onClick={() => toggle(key)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#FAFAFB] transition-colors"
                    data-testid={`store-family-toggle-${key}`}
                  >
                    <span
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-[16px]"
                      style={{ background: `${meta.accent}12`, border: `1px solid ${meta.accent}28`, color: meta.accent }}
                    >
                      {meta.emoji}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-[700] text-[13.5px] text-[#0F0F10] truncate">{meta.title}</p>
                        <TrendChip deltaPct={p.delta_pct} />
                      </div>
                      <p className="text-[11.5px] text-[#4B4B52] mt-0.5 tabular-nums">
                        {fmtUSD(p.gross)} · {fmtInt(p.orders)} orders · {fmtInt(p.units)} units
                        {p.refunded_gross > 0 && (
                          <span className="text-[#B91C1C] ml-2">− {fmtUSD(p.refunded_gross)} refunded</span>
                        )}
                      </p>
                    </div>
                    {(p.bundles?.length ?? 0) > 0 && (
                      isOpen
                        ? <ChevronUp   size={16} className="text-[#8A8A93] flex-shrink-0"/>
                        : <ChevronDown size={16} className="text-[#8A8A93] flex-shrink-0"/>
                    )}
                  </button>

                  {isOpen && (p.bundles?.length ?? 0) > 0 && (
                    <div className="border-t border-[#F0F0F1] px-4 py-3 space-y-2" data-testid={`store-family-bundles-${key}`}>
                      <div className="grid grid-cols-4 gap-2 text-[10px] font-[700] uppercase tracking-[0.1em] text-[#8A8A93] pb-1 border-b border-[#F0F0F1]">
                        <span>Bundle</span>
                        <span className="text-right">Orders</span>
                        <span className="text-right">Units</span>
                        <span className="text-right">Gross</span>
                      </div>
                      {[...(p.bundles || [])]
                        .sort((a, b) => bundleQty(a.key) - bundleQty(b.key))
                        .map((b) => (
                          <div
                            key={b.key}
                            className="grid grid-cols-4 gap-2 text-[12px] text-[#0F0F10] items-center"
                            data-testid={`store-bundle-row-${b.key}`}
                          >
                            <span className="font-[600]">{bundleQty(b.key)} {meta.unitLabel}</span>
                            <span className="text-right tabular-nums text-[#4B4B52]">{fmtInt(b.orders)}</span>
                            <span className="text-right tabular-nums text-[#4B4B52]">{fmtInt(b.units)}</span>
                            <span className="text-right font-[700] tabular-nums">{fmtUSD(b.gross)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Best sellers */}
          {(data.best_sellers?.length ?? 0) > 0 && (
            <div className="mt-6" data-testid="store-best-sellers">
              <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93] mb-2">
                TOP 5 SKUS · {PERIODS.find(p => p.key === period)?.label}
              </p>
              <div className="rounded-2xl bg-white overflow-hidden" style={{ border: '1px solid rgba(15,15,16,0.06)' }}>
                {data.best_sellers.map((b, i) => {
                  const meta = FAMILY_META[b.product_type === 'intro' ? 'intros' : b.product_type === 'super_like' ? 'super_likes' : 'boosts'];
                  return (
                    <div
                      key={b.key}
                      className={`flex items-center gap-3 p-3 ${i > 0 ? 'border-t border-[#F0F0F1]' : ''}`}
                      data-testid={`store-best-seller-${i}`}
                    >
                      <span className="w-6 text-center text-[11px] font-[700] text-[#8A8A93] tabular-nums">#{i + 1}</span>
                      <span
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-[13px] flex-shrink-0"
                        style={{ background: `${meta?.accent}12`, color: meta?.accent }}
                      >
                        {meta?.emoji}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12.5px] font-[700] text-[#0F0F10] truncate">
                          {bundleQty(b.key)} {meta?.unitLabel}
                        </p>
                        <p className="text-[11px] text-[#8A8A93] tabular-nums">
                          {fmtInt(b.orders)} orders · {fmtInt(b.units)} units
                        </p>
                      </div>
                      <p className="font-[700] text-[13.5px] text-[#0F0F10] tabular-nums flex-shrink-0">
                        {fmtUSD(b.gross)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : null}

      <p className="text-[10.5px] text-[#8A8A93] mt-4 leading-snug">
        Add-on sales are one-time, so they roll into gross but not MRR/ARR. Refunded gross is
        tracked separately once Stripe webhook wiring is complete.
      </p>
    </section>
  );
}

function WarmerStatus({ warmer }) {
  if (!warmer) return null;
  const failing = (warmer.consecutive_failures || 0) >= 2;
  const last = warmer.last_success_at ? new Date(warmer.last_success_at) : null;
  const attempt = warmer.last_attempt_at ? new Date(warmer.last_attempt_at) : null;
  const now = Date.now();
  const minsAgo = last ? Math.max(0, Math.round((now - last.getTime()) / 60000)) : null;
  const relStr = minsAgo == null ? 'never'
    : minsAgo < 1 ? 'just now'
    : minsAgo === 1 ? '1 min ago'
    : minsAgo < 60 ? `${minsAgo} min ago`
    : `${Math.round(minsAgo / 60)}h ago`;
  const bg = failing ? '#FEF2F2' : '#F0FDF4';
  const border = failing ? '#FCA5A5' : '#A7F3D0';
  const fg = failing ? '#B91C1C' : '#047857';
  const dotBg = failing ? '#B91C1C' : '#10B981';
  return (
    <div
      className="rounded-2xl flex items-center justify-between gap-3"
      style={{ background: bg, border: `1px solid ${border}`, padding: '10px 14px' }}
      data-testid="warmer-status"
      title={warmer.last_error ? `Last error: ${warmer.last_error}` : ''}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ background: dotBg, boxShadow: failing ? 'none' : `0 0 0 4px ${dotBg}22` }}
        />
        <span style={{ color: fg, fontSize: 12, fontWeight: 700 }}>
          {failing
            ? `⚠ Warmer failing — ${warmer.consecutive_failures} in a row`
            : `Warmer OK · last ${relStr}`}
        </span>
      </div>
      <span style={{ color: fg, fontSize: 11, fontWeight: 700 }} className="tabular-nums">
        {fmtInt(warmer.runs_today)} today
      </span>
    </div>
  );
}

function StorePill({ emoji, title, lifetime, last30d, accent, testId }) {  const life = lifetime || { gross: 0, units: 0, count: 0 };
  const l30 = last30d || { gross: 0, units: 0, count: 0 };
  return (
    <div
      className="rounded-2xl p-4 border"
      style={{ borderColor: '#0000000f', background: '#FFFFFF' }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-[13px]"
          style={{ background: accent }}
        >{emoji}</span>
        <span className="text-[12.5px] font-semibold text-[#0F0F10]">{title}</span>
      </div>
      <p className="font-brand text-[22px] font-[500] leading-none text-[#0F0F10]">
        {fmtUSD(life.gross)}
      </p>
      <p className="text-[11px] text-[#8A8A93] mt-1">
        {fmtInt(life.units)} units · {fmtInt(life.count)} orders
      </p>
      <div className="mt-3 pt-3 border-t border-black/[0.06] flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#8A8A93]">30d</span>
        <span className="text-[12px] font-bold tabular-nums text-[#0F0F10]">
          {fmtUSD(l30.gross)} · {fmtInt(l30.units)}
        </span>
      </div>
    </div>
  );
}
